import React, { useMemo } from 'react';
import {
  BasicContainer,
  TypographyProps,
  ButtonAsWrapper,
  FlexCol,
  FlexRow,
  TextDynamic,
  FlexProps,
} from '@minecraft.atoms';

type NavItemProps<NavKey extends string> = {
  /** the title renderd on the tab */
  navTitle: string;
  /** the unique key value used to identify the tab, this is the value returned in onTabChange */
  key: NavKey;
};

type SubNavSize = 'sm' | 'lg';

export type SubNavProps<NavKey extends string> = {
  /** the key value for the active nav item */
  activeNavItem: NavKey;
  /** the list of nav configuration objects, the activeNavItem prop should equal the value of one of the keys */
  navItems: NavItemProps<NavKey>[];
  /** the callback to be called when a new nav is selected, can be by keyboard, touch or click */
  onNavChange?: (navKey: NavKey) => void;
  /** */
  'data-qa-id'?: string;
  /** */
  'data-testid'?: string;
  /** aria-label for this subnav (like what it's the subnav for??) */
  'aria-label': string;
  /** prop to control the size of the nav items & space between, defaults to "lg" */
  size?: SubNavSize;
  /** gap between nav items defaults to 8 (2rem) */
  gap?: FlexProps['gap'];
};

const validateNavItemsProp = <NavKey extends string>({
  navItems,
  componentName,
}: {
  navItems: NavItemProps<NavKey>[];
  componentName: string;
}) => {
  const arr = Array.isArray(navItems) ? navItems : [];
  const keys = [];
  return arr.filter((nav, index) => {
    if (!nav.navTitle) {
      console.warn(`${componentName}: nav with index ${index} is missing navTitle value`);
      return false;
    }

    if (!nav.key) {
      console.warn(`${componentName}: nav with index ${index} is missing key value`);
      return false;
    }

    if (keys.includes(nav.key)) {
      console.warn(`${componentName}: found multiple navItems with key ${nav.key}`);
      return false;
    }

    keys.push(nav.key);
    return true;
  });
};

const SIZE_CONFIGS: Record<SubNavSize, { fontSize: TypographyProps['fontSize'] }> = {
  sm: {
    fontSize: 'detailLarge',
  },
  lg: {
    fontSize: 'bodyRegular',
  },
};
/**
 * SubNav takes a list of objects that describe each nav item, as well as the active nav and a callback when a nav is clicked
 */
export const SubNav = <NavKey extends string>({
  activeNavItem,
  'data-qa-id': dataQaId,
  'data-testid': dataTestId,
  onNavChange = () => null,
  navItems,
  'aria-label': ariaLabel,
  size: sizeProp = 'lg',
  gap = '8',
}: SubNavProps<NavKey>) => {
  const navsTouse = useMemo(() => validateNavItemsProp({ navItems, componentName: 'SubNav' }), [navItems]);
  const size = useMemo(() => {
    if (['sm', 'lg'].includes(sizeProp)) {
      return sizeProp;
    }
    return 'lg';
  }, [sizeProp]);

  return (
    <FlexCol data-qa-id={dataQaId} data-testid={dataTestId}>
      <FlexRow
        maxHeight={3}
        role="navigation"
        className="cn_overflow-x-auto"
        alignItems="flex-end"
        wrap="nowrap"
        gap={gap}
        w="full"
        aria-label={ariaLabel}
      >
        {navsTouse.map((nav) => {
          const isActive = nav.key === activeNavItem;
          return (
            <ButtonAsWrapper
              aria-label={nav.navTitle}
              aria-selected={isActive}
              data-qa-id={`${nav.key}-tab`}
              disabled={isActive}
              key={nav.key}
              onClick={() => onNavChange(nav.key)}
            >
              <BasicContainer
                key={nav.key}
                borderPlacement="bottom"
                borderColor={isActive ? 'emphasis' : 'transparent'}
                borderSize={0.09}
                className="cn_atom_py-2"
              >
                <TextDynamic
                  as="p"
                  noWrap
                  fontWeight={isActive ? 'semibold' : 'medium'}
                  color={isActive ? 'default' : 'primary'}
                  fontSize={SIZE_CONFIGS[size].fontSize}
                >
                  {nav.navTitle}
                </TextDynamic>
              </BasicContainer>
            </ButtonAsWrapper>
          );
        })}
      </FlexRow>
    </FlexCol>
  );
};
